import { useState, type FormEvent } from 'react';
import { BRAND_LOGO, BRAND_NAME } from '@/lib/brand';
import { Navigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Field, Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/misc';
import { useAuth } from '@/lib/auth';
import { errorText } from '@/lib/errors';
import { DesktopDownload } from '@/components/layout/DesktopDownload';

const EMAIL_KEY = 'login.rememberedEmail';
const readEmail = () => {
  try {
    return localStorage.getItem(EMAIL_KEY) ?? '';
  } catch {
    return '';
  }
};

export function LoginPage() {
  const { status, login } = useAuth();
  const [email, setEmail] = useState(readEmail);
  const [remember, setRemember] = useState(true);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  if (status === 'authenticated') return <Navigate to="/inbox" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await login(email, password, remember);
      try {
        if (remember) localStorage.setItem(EMAIL_KEY, email);
        else localStorage.removeItem(EMAIL_KEY);
      } catch {
        // storage blocked: nothing to remember
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-full flex-col items-center justify-center bg-chat-bg p-4 pb-12">
      <Card className="w-full max-w-sm p-6">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          {BRAND_LOGO && <img src={BRAND_LOGO} alt={BRAND_NAME} className="h-20 w-auto object-contain dark:invert" />}
          <h1 className="text-xl font-semibold tracking-tight">{BRAND_NAME}</h1>
          <p className="text-sm text-muted-foreground">Sign in to manage WhatsApp orders</p>
        </div>
        <form onSubmit={submit} className="grid gap-4">
          <Field label="Email">
            <Input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus={!email} />
          </Field>
          <Field label="Password">
            <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus={!!email} />
          </Field>
          <label className="flex cursor-pointer items-center gap-2 text-sm" title="Unticked: you're signed out when the browser closes (use on shared computers)">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="size-4 accent-primary" />
            Remember me
          </label>
          {error ? <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p> : null}
          <Button type="submit" disabled={busy} className="w-full">
            {busy ? <Spinner /> : null} Sign in
          </Button>
        </form>
      </Card>
      <DesktopDownload className="mt-4" />
      <p className="absolute inset-x-0 bottom-4 text-center text-xs text-muted-foreground">
        Powered by{' '}
        <a href="https://capricon.biz" target="_blank" rel="noopener noreferrer" className="font-medium text-foreground underline-offset-2 hover:underline">
          Capricon Solutions
        </a>
      </p>
    </div>
  );
}
